import * as moment from 'moment';
import { Component, Inject, Input, LOCALE_ID, OnInit } from '@angular/core';
import { EMPTY, Subscription, forkJoin } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { expand, finalize, pairwise } from 'rxjs/operators';
import { ActivatedRoute } from '@angular/router';

import { ChartDataset, ChartLabel } from 'src/app/core/entities/chart-dataset';
import { AutoUnsub } from 'src/app/core/decorators/auto-unsub.decorator';
import { ChartDataPoint } from 'src/app/core/entities/chart-data-point.entity';
import { ChartDescription } from 'src/app/core/entities/chart-description';
import { ChartEvaluation } from 'src/app/core/entities/chart-evaluation';
import { ChartService } from 'src/app/core/services/chart.service';
import { EvaluationService } from 'src/app/core/services/evaluation.service';
import { Feature } from 'src/app/core/entities/feature.entity';
import { FeatureService } from 'src/app/core/services/feature.service';
import { Patient } from 'src/app/core/entities/patient.entity';

@Component({
  selector: 'app-phq8',
  templateUrl: './phq8.component.html',
  styleUrls: ['./phq8.component.css'],
})
@AutoUnsub()
export class Phq8Component implements OnInit {
  @Input() public patientProfile: Patient = new Patient();
  private _observers: Subscription = new Subscription();
  private _features: Feature[] = [];
  public evaluation: ChartEvaluation = {
    evaluationCode: EvaluationService.EVALUATION_PHQ8,
    features: [
      {
        featureId: FeatureService.FEATURE_PHQ8_SCR,
        label: $localize`:@@patient_evaluationPHQ8_featurePHQ8SCRLabel:Score`,
        min: 0,
        max: 24,
        chartDatasetOption: {
          type: 'bar',
          color: '#8893aa',
          nbSteps: 12,
          tooltip: {
            label: $localize`:@@patient_evaluationPHQ8_featurePHQ8SCRTooltipLabel:Score`,
            unit: '',
          },
        },
      },
    ],
    horizontalLimitLines: [
      {
        label: $localize`:@@patient_evaluationPHQ8_horizontalLimitLineYLabel:Maximum severity`,
        color: '#3d3d40',
        assignToAxisIds: {
          y: 24,
        },
      },
    ],
  };

  public datasets: ChartDataset[] = [];
  public xLabels: ChartLabel[] = [];
  public chartDescription: ChartDescription;
  public dateRangeForm: FormGroup;
  public loading = true;
  public loadingChart = true;
  public measuringPointsTotal = 0;
  public progression = 0;

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    private featureService: FeatureService,
    private chartService: ChartService,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.prepareDateRangeForm();
  }

  /**
   * Prepares date range form
   */
  prepareDateRangeForm(): void {
    const startDate = moment.utc().subtract(12, 'months').format('MM/YYYY');
    const endDate = moment.utc().format('MM/YYYY');

    this.dateRangeForm = this.formBuilder.group({
      startDate: [
        startDate,
        {
          validators: [
            Validators.required,
            Validators.pattern(/^(0[1-9]|1[012])\/[0-9]{4}$/),
          ],
          updateOn: 'blur',
        },
      ],
      endDate: [
        endDate,
        {
          validators: [
            Validators.required,
            Validators.pattern(/^(0[1-9]|1[012])\/[0-9]{4}$/),
          ],
          updateOn: 'blur',
        },
      ],
    });

    this._observers.add(
      this.dateRangeForm.valueChanges.pipe(pairwise()).subscribe({
        next: ([previousRes, res]) => this.submitDateRangeForm(),
      })
    );

    this.loading = false;
  }

  /**
   * Changes date range
   * @param event
   * @param key
   */
  changeDateRange(
    event: { monthIndex: number; year: number },
    key: string
  ): void {
    const value = moment
      .utc([event.year, event.monthIndex, 1])
      .format('MM/YYYY');

    this.dateRangeForm.get(key).setValue(value);
  }

  /**
   * Gets date year
   * @param key
   * @returns year
   */
  getDateYear(key: string): number {
    let splitDate = this.dateRangeForm.get(key).value.split('/');
    return splitDate[1];
  }

  /**
   * Gets date month
   * @param key
   * @returns month
   */
  getDateMonth(key: string): number {
    let splitDate = this.dateRangeForm.get(key).value.split('/');
    return parseInt(splitDate[0], 10) - 1;
  }

  /**
   * Submits date range
   */
  submitDateRangeForm(): void {
    if (this.dateRangeForm.valid) {
      this.loadingChart = true;
      this.setChart();
    }
  }

  /**
   * Sets chart
   */
  setChart(): void {
    this._features = [];
    this.datasets = [];
    this.xLabels = [];

    const startDate = moment.utc(
      this.dateRangeForm.get('startDate').value,
      'MM/YYYY'
    );
    const endDate = moment.utc(
      this.dateRangeForm.get('endDate').value,
      'MM/YYYY'
    );

    let params = {
      page: 1,
      startDate: startDate.toISOString(),
      endDate: endDate.endOf('month').toISOString(),
      patientUuid: this.route.snapshot.params['uuid'],
      featureIds: this.evaluation.features.map((item) => item.featureId),
    };

    const source$ = this.featureService.getFeatures(params).pipe(
      expand((res) => {
        params.page++;
        this._features = this._features.concat(res);
        return res.length < 50
          ? EMPTY
          : this.featureService.getFeatures(params);
      })
    );

    forkJoin([source$])
      .pipe(
        finalize(() => {
          this.loadingChart = false;
        })
      )
      .subscribe((res) => {
        let chartDataPoints = {};

        this.evaluation.features.forEach((feature) => {
          let groupedFeatures = this.featureService.groupFeatures(
            this._features,
            feature.featureId
          );
          chartDataPoints[feature.featureId] =
            this.chartService.setChartDataPoints(groupedFeatures);
        });

        if (Object.values(chartDataPoints).length > 0) {
          // Set labels
          const maxDataPoints = Object.values(chartDataPoints).reduce(
            (r: any, s: any) => (r.length > s.length ? r : s),
            0
          );
          this.xLabels = this.chartService.setXLabels(
            maxDataPoints as ChartDataPoint[],
            false
          );

          // Set datasets
          this.evaluation.features.forEach((feature) => {
            let dataset = this.chartService.setDataset(
              feature,
              chartDataPoints[feature.featureId],
              feature.chartDatasetOption,
              [],
              this.xLabels,
              feature.min ??
                (chartDataPoints[feature.featureId].length
                  ? chartDataPoints[feature.featureId][0].min
                  : null),
              feature.max ??
                (chartDataPoints[feature.featureId].length
                  ? chartDataPoints[feature.featureId][0].max
                  : null),
              feature.isMaxLimited,
              feature.isMinLimited
            );
            this.datasets.push(dataset);
          });

          if (this.datasets[0]) {
            // Set measuring points total
            this.measuringPointsTotal = this.datasets[0].data.filter(
              (item) => item.value !== null
            ).length;

            // Set progression
            this.progression = this.chartService.getProgression(
              this.datasets[0].data,
              true
            );
          }
        }
      });
  }
}
